import { em } from 'polished'
import styled from 'styled-components'

import {
  TextFieldError,
  TextFieldPlaceholder,
  TextFieldWrapper,
  Underline,
} from 'components/_form/TextField/styles'
import { TextLH } from 'styles/components/typography'

export const H4Styled = styled.h4`
  margin: 0 0 20px;
  font-family: ${p => p.theme.typography.mediumFont};
  font-size: ${em(22)};
  line-height: ${em(30, 22)};
  font-weight: 400;

  ${p => p.theme.mq.upMd} {
    font-size: ${em(26)};
    line-height: ${em(35, 26)};
  }
`

export const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${em(40)};
  margin-bottom: ${em(62)};

  ${p => p.theme.mq.upMd} {
    grid-template-columns: ${em(321)} ${em(321)};
    column-gap: ${em(41)};
    row-gap: ${em(40)};
    justify-content: start;
    margin-bottom: ${em(80)};
  }
`

export const FieldHint = styled.p`
  margin: ${em(33)} 0 0;
  color: ${p => p.theme.colors.blackOp4};
  font-size: ${em(14)};
  line-height: ${em(20, 14)};

  ${p => p.theme.mq.upMd} {
    position: absolute;
    top: 91px;
    left: 0;
    width: 317px;
    margin: 0;
  }
`

export const ContactField = styled.div`
  position: relative;
  min-width: 0;

  > ${TextFieldWrapper} {
    display: block;
    margin: 0;
    padding-top: ${em(33)};

    ${p => p.theme.mq.upMd} {
      min-height: ${em(70)};
    }
  }

  > ${TextFieldWrapper} > input {
    height: ${em(25)};
    line-height: ${em(25, 16)};
    font-size: ${em(16)};
  }

  > ${TextFieldWrapper} > ${TextFieldPlaceholder} {
    top: ${em(33)};
    left: 0;
    font-size: ${em(16)};
    line-height: ${em(25, 16)};
    color: ${p => p.theme.colors.blackOp4};
    transform: none;
    transition-property: top, font-size, line-height;
    transition-duration: ${p => p.theme.animation.timeStandard};
    transition-timing-function: ${p => p.theme.animation.cubicBezier};
  }

  > ${TextFieldWrapper}:focus-within > ${TextFieldPlaceholder},
  > ${TextFieldWrapper}[data-has-value='true'] > ${TextFieldPlaceholder},
  > ${TextFieldWrapper}[data-has-mask='true'] > ${TextFieldPlaceholder} {
    top: 0;
    font-size: ${em(14)};
    line-height: ${em(20, 14)};
    transform: none;
  }

  > ${TextFieldWrapper} > ${Underline} {
    top: ${em(8)};
    height: ${em(2)};
    background: transparent;

    &:before {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      width: 100%;
      height: 1px;
      background: ${p => p.theme.colors.blackOp1};
      transform: translateY(-50%);
    }

    &:after {
      top: 50%;
      height: 1px;
      transform: translateY(-50%);
    }
  }

  > ${TextFieldWrapper}[data-has-error='true'] > ${Underline} {
    background: transparent;

    &:before {
      background: #e961a0;
    }
  }

  > ${TextFieldWrapper} > ${TextFieldError} {
    top: calc(100% + ${em(11)});
    height: auto;
    overflow: visible;
    color: #e961a0;
    font-size: ${em(14)};
    line-height: normal;
    white-space: nowrap;
  }

  ${p => p.theme.mq.upMd} {
    > ${TextFieldWrapper}[data-has-error='true'] ~ ${FieldHint} {
      top: 103px;
    }
  }
`

export const PhoneFieldWrap = styled(ContactField)`
  ${p => p.theme.mq.upMd} {
    grid-column: 1;
  }
`

export const DeliveryRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${em(20)};
  margin-bottom: ${em(60)};

  ${p => p.theme.mq.upMd} {
    display: grid;
    grid-template-columns: minmax(0, ${em(349)}) max-content;
    column-gap: ${em(46)};
    row-gap: 0;
    align-items: center;
    position: relative;
    top: 1px;
    width: ${em(669)};
    margin-bottom: ${em(81)};
  }
`

export const DeliveryChoice = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${em(10)};

  label {
    height: auto;
    align-items: center;
  }

  label > span:nth-of-type(2) {
    margin-right: ${em(20)};
    font-size: ${em(16)};
    line-height: ${em(25, 16)};
  }

  label > div {
    gap: ${em(20)};
    margin-left: 0 !important;
    margin-top: 0 !important;
    width: auto !important;
    transform: none !important;
  }
`

export const DeliveryAddress = styled.div`
  > span {
    display: block;
    color: ${p => p.theme.colors.blackOp3};
    font-size: ${em(16)};
    line-height: ${em(25, 16)};
  }
`

export const DeliveryButtonWrap = styled.div`
  align-self: flex-start;
  display: inline-flex;
  width: fit-content;
  min-width: 0;
  max-width: 100%;

  .delivery-map-button {
    display: inline-block;
    box-sizing: border-box;
    width: auto;
    min-width: 0;
    max-width: none;
    height: ${em(40)};
    min-height: ${em(40)};

    ${p => p.theme.mq.upMd} {
      width: ${em(274)};
      height: ${em(50)};
      min-height: ${em(50)};
    }
  }

  .delivery-map-button > .bg,
  .delivery-map-button > .blackout {
    border-radius: ${em(100)};
    height: 100%;
  }

  .delivery-map-button > .bg {
    border: 1px solid ${p => p.theme.colors.blackOp15};
    background: transparent;
  }

  .delivery-map-button > .blackout,
  .delivery-map-button > .blackout.bg--purple {
    background: rgba(0, 0, 0, 0.04);
  }

  .delivery-map-button > .text {
    box-sizing: border-box;
    top: 0;
    width: auto;
    height: 100%;
    padding: ${em(7)} ${em(22)} ${em(8)};
    align-items: center;
    justify-content: center;

    ${p => p.theme.mq.upMd} {
      padding: ${em(11)} ${em(28)} ${em(14)};
    }
  }

  .delivery-map-button > .text span {
    display: block;
    margin: 0;
    font-family: ${p => p.theme.typography.baseFont};
    font-size: ${em(16)};
    font-weight: 400;
    line-height: ${em(25, 16)};
    letter-spacing: 0;
    white-space: nowrap;
    color: ${p => p.theme.colors.black};
  }

  html:not(.no-hovers) .button-rounded-base.delivery-map-button:hover > .text,
  html:not(.no-hovers) .button-rounded-base.delivery-map-button:hover > .text span {
    color: ${p => p.theme.colors.black} !important;
  }

  ${p => p.theme.mq.upMd} {
    align-self: center;
  }
`

export const PaymentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${em(20)};
  margin-bottom: ${em(60)};

  ${p => p.theme.mq.upMd} {
    margin-bottom: ${em(80)};
  }
`

export const PaymentRow = styled.div`
  label {
    height: auto;
    align-items: center;
    flex-wrap: wrap;
  }

  label > span:nth-of-type(2) {
    margin-right: 0;
    font-size: ${em(16)};
    line-height: ${em(25, 16)};

    ${p => p.theme.mq.upMd} {
      margin-right: ${em(20)};
    }
  }

  label > div {
    gap: ${em(15)} !important;
    margin-top: ${em(5)} !important;
    margin-left: ${em(30)} !important;
    width: auto;
    transform: none !important;

    ${p => p.theme.mq.upMd} {
      gap: ${em(20)} !important;
      margin-top: 0 !important;
      margin-left: ${em(20)} !important;
    }
  }
`

export const ConsentBlock = styled.div`
  font-family: ${p => p.theme.typography.baseFont};
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  letter-spacing: 0;
  color: ${p => p.theme.colors.blackOp4};
`

export const ConsentIntro = styled.div`
  margin-bottom: 20px;
  font-family: ${p => p.theme.typography.baseFont};
  color: inherit;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  letter-spacing: 0;
`

export const ConsentRow = styled.div`
  & + & {
    margin-top: 20px;
  }

  > div {
    margin: 0;
  }

  > div > label {
    display: block;
    width: 100%;
  }

  > div > label > div {
    width: 100%;
    align-items: flex-start;
  }

  &.consent-row--centered > div > label > div {
    align-items: center;
  }

  > div > label > div > div:first-child {
    top: 0;
    width: 20px;
    height: 20px;
    margin-right: 15px;
    border-radius: 4px;
    border-color: ${p => p.theme.colors.blackOp3};
    flex-shrink: 0;
  }

  > div > label > div > span {
    display: block;
    flex: 1 0 0;
    min-width: 0;
    font-family: ${p => p.theme.typography.baseFont};
    color: ${p => p.theme.colors.blackOp4};
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    letter-spacing: 0;
  }

  > div > label > div > span > span,
  > div > label > div > span a {
    font-family: ${p => p.theme.typography.baseFont};
    color: inherit;
    font-size: inherit;
    line-height: inherit;
    font-weight: inherit;
    letter-spacing: inherit;
  }
`

export const TextLHStyled = styled(TextLH)`
  font-family: ${p => p.theme.typography.baseFont};
  color: ${p => p.theme.colors.blackOp4};
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  letter-spacing: 0;

  ${p => p.theme.mq.upMd} {
    font-size: 14px;
    line-height: 20px;
  }
`
